import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, ExternalLink, Flex, Text } from 'src/packages/core';
import { BadgeType } from 'src/packages/core/badge/Badge.types';
import { DarkTheme, LightTheme, ThemeType, useTheme } from 'src/packages/theme';
import {
  AvatarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  KYCIcon,
  KYCIconDark,
} from 'src/packages/icons';

import { ProfileIconLinks } from '../links';
import { HeaderUser, UserVerificationStatusEnum } from '../Header.types';
import { config } from 'src/config';

interface ProfileIconPopoverContentProps {
  theme: ThemeType;
  user?: HeaderUser;
  logout?: () => void;
  copyToClipboard?: (value: string) => void;
}

export const ProfileIconPopoverContent = ({
  user,
  theme,
  logout,
  copyToClipboard,
}: ProfileIconPopoverContentProps) => {
  const { t } = useTranslation();
  const contextTheme = useTheme();
  const ChevronIcon = contextTheme.rtl ? ChevronLeftIcon : ChevronRightIcon;

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  const KYCStatusBadge = useMemo(() => {
    if (!user) {
      return null;
    }

    const icon = theme === 'light' ? KYCIcon : KYCIconDark;
    let type: BadgeType;
    let text;

    switch (user.kycStatus) {
      case UserVerificationStatusEnum.VERIFIED:
        type = 'success';
        text = t('verified');
        break;
      case UserVerificationStatusEnum.NOT_VERIFIED:
        type = 'error';
        text = t('unverified');
        break;
      case UserVerificationStatusEnum.REFUSED:
        type = 'error';
        text = t('verification_refused');
        break;
      case UserVerificationStatusEnum.UNDER_REVIEW:
        type = 'pending';
        text = t('in_review');
        break;
      default:
        type = 'error';
        text = t('unverified');
        break;
    }

    return <Badge type={type} text={text} IconComponent={icon} theme={theme} />;
  }, [user, theme]);

  return useMemo(
    () => (
      <>
        <Flex
          alignItems={'center'}
          p={4}
          width={'100%'}
          backgroundColor={defaultTheme.colors.background.secondary.BG4}
          gap={5}
        >
          <AvatarIcon size={40} />

          <Flex flexDirection={'column'} gap={2}>
            <Text
              variant={'p2'}
              text={user?.email}
              fontFamily={'Inter-Medium'}
              color={
                theme === 'light' ? LightTheme.colors.text.primary : DarkTheme.colors.text.primary
              }
            />

            <Flex gap={2}>
              <Text
                variant={'p3'}
                text={`UID ${user?.id}`}
                color={
                  theme === 'light'
                    ? LightTheme.colors.text.tertiary
                    : DarkTheme.colors.text.secondary
                }
              />

              {user?.showUserInfo && (
                <Flex
                  style={{
                    cursor: copyToClipboard ? 'pointer' : 'unset',
                  }}
                  onClick={async () => {
                    if (copyToClipboard) {
                      await copyToClipboard(user.id);
                    }
                  }}
                >
                  <CopyIcon
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.tertiary
                        : DarkTheme.colors.text.secondary
                    }
                  />
                </Flex>
              )}
            </Flex>

            {KYCStatusBadge}
          </Flex>

          <ExternalLink link={`${config.coinflareUrl}/user`}>
            <ChevronIcon color={defaultTheme.colors.text.primary} />
          </ExternalLink>
        </Flex>

        <ul style={{ background: defaultTheme.colors.background.secondary.BG3 }}>
          {ProfileIconLinks.map((item, index) => {
            if (item.restricted && item.restricted()) {
              return null;
            }

            return (
              <ExternalLink
                key={index}
                link={item.url}
                py={4}
                px={5}
                target={item.target ?? '_self'}
              >
                <Text
                  variant={'p2'}
                  text={t(item.label)}
                  color={defaultTheme.colors.text.primary}
                />
              </ExternalLink>
            );
          })}
        </ul>

        <Flex
          p={5}
          justifyContent={'center'}
          alignItems={'center'}
          width={'100%'}
          backgroundColor={defaultTheme.colors.background.secondary.BG3}
        >
          <Button
            theme={theme}
            variant={'tertiaryDefault'}
            text={t('button.logout')}
            width={'100%'}
            onClick={() => {
              logout && logout();
            }}
          />
        </Flex>
      </>
    ),
    [user, theme, copyToClipboard, logout]
  );
};
